// src/pages/ReportingHub.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { Link, useNavigate } from "react-router-dom";
import { FaChartBar, FaChevronRight } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faChartBar as faChartBarIcon,
  faUsers,
  faListAlt,
  faTicketAlt,
} from "@fortawesome/free-solid-svg-icons";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

/* ------------------------------ helpers ---------------------------------- */
function readUser() {
  try { return JSON.parse(localStorage.getItem("user")) || {}; } catch { return {}; }
}
async function toJsonSafe(res) {
  const t = await res.text();
  try { return JSON.parse(t); } catch { throw new Error(t.slice(0, 240)); }
}

/** Project status -> shared design-system pill variant. */
const statusPillClass = (s) => {
  const k = String(s || "new").toLowerCase();
  if (k === "approved") return "pill pill-success";
  if (k === "pending") return "pill pill-warning";
  if (k === "rejected") return "pill pill-danger";
  return "pill pill-info";
};

// leader/creator SLIIT ID
const pickLeaderSliitId = (p) =>
  p.created_by_sliitid || p.project_officer_sliitid || p.created_by || p.owner || "";

/** SUPER robust phase resolver with default of 1 (matches your DB default). */
function getPhaseCount(p) {
  const directKeys = [
    "phase_count","PhaseCount","phaseCount",
    "current_phase","currentPhase",
    "phase","total_phases","totalPhases",
  ];
  for (const key of directKeys) {
    if (p[key] !== undefined && p[key] !== null && p[key] !== "") {
      const n = Number(p[key]); if (Number.isFinite(n) && n >= 0) return n;
    }
  }
  const nests = ["meta","details","data","extra"];
  for (const root of nests) {
    const obj = p[root];
    if (obj && typeof obj === "object") {
      for (const key of directKeys) {
        if (obj[key] !== undefined && obj[key] !== null && obj[key] !== "") {
          const n = Number(obj[key]); if (Number.isFinite(n) && n >= 0) return n;
        }
      }
    }
  }
  if (Array.isArray(p.phases)) return p.phases.length;
  return 1;
}

/** case-insensitive map of { id: name } */
function normalizeNameMap(rawMap = {}) {
  const out = {};
  for (const [id, name] of Object.entries(rawMap)) {
    const s = String(id);
    out[s] = name;
    out[s.toUpperCase()] = name;
    out[s.toLowerCase()] = name;
    out[s.trim()] = name;
  }
  return out;
}

/* -------------------------------- Component ------------------------------- */
export default function ReportingHub() {
  const navigate = useNavigate();
  const userRaw = useMemo(() => readUser(), []);
  const user = userRaw?.name ? userRaw : { name: "Unknown User", role: "Member", image: "/user.png" };
  const isAdmin = /admin/i.test(String(user?.role || "")) || user?.is_admin === 1 || user?.isAdmin === 1;

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);

  const [nameMap, setNameMap] = useState({});
  const [nameLoading, setNameLoading] = useState(false);

  /* ------------------------------- load projects -------------------------- */
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        setErr("");
        setLoading(true);
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_all.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            SLIITID: user?.SLIITID || null,
            include_roles: true,
            admin_mode: isAdmin ? 1 : 0,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await toJsonSafe(res);
        const list = Array.isArray(data?.projects) ? data.projects : [];

        const toneOrder = (s) => {
          const k = String(s || "").toLowerCase();
          if (k === "approved") return 0;
          if (k === "pending") return 1;
          if (k === "new") return 2;
          if (k === "rejected") return 3;
          return 4;
        };
        list.sort((a, b) => {
          const ta = toneOrder(a.status), tb = toneOrder(b.status);
          if (ta !== tb) return ta - tb;
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        });

        if (!off) setProjects(list);
      } catch (e) {
        if (!off) { setErr("Unable to fetch projects"); setProjects([]); }
      } finally {
        if (!off) setLoading(false);
      }
    })();
    return () => { off = true; };
  }, [user?.SLIITID, isAdmin]);

  /* ------------- resolve SLIIT IDs -> human names ------------------------ */
  const nameMapRef = useRef(nameMap);
  useEffect(() => { nameMapRef.current = nameMap; }, [nameMap]);

  useEffect(() => {
    const ids = Array.from(
      new Set(projects.map((p) => String(pickLeaderSliitId(p) || "").trim()).filter(Boolean))
    ).filter((id) => !(id in nameMapRef.current));
    if (ids.length === 0) return;

    let off = false;
    (async () => {
      try {
        setNameLoading(true);
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/memberbasic_api.php?action=names_by_ids`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        });
        const data = await toJsonSafe(res);
        if (data?.success && data?.data && typeof data.data === "object") {
          const resolved = normalizeNameMap(data.data);
          if (!off && Object.keys(resolved).length > 0) {
            setNameMap((m) => ({ ...m, ...resolved }));
          }
        }
      } catch {
        // ignore
      } finally {
        if (!off) setNameLoading(false);
      }
    })();

    return () => { off = true; };
  }, [projects]);

  /* ----------------------------------- UI --------------------------------- */
  return (
    <>
      <Sidebar />
      <Topbar user={user} />

      <main className="lg:ml-64 mt-14 ml-0 px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        {/* Header */}
        <div className="page-head">
          <div>
            <h1>Reporting Hub</h1>
            <p className="page-desc">
              Browse every club project, then drill in for treasurer &amp; secretary reports.
            </p>
          </div>
          <div className="filter-row">
            <span className="chip active">
              {loading ? "…" : `${projects.length} project${projects.length === 1 ? "" : "s"}`}
              {nameLoading ? " · resolving names…" : ""}
            </span>
          </div>
        </div>

        {/* Quick shortcuts */}
        <div className="filter-row" style={{ flexWrap: "wrap", marginBottom: "var(--sp-5)" }}>
          <Link to="/club-statistics" className="chip">
            <FontAwesomeIcon icon={faChartPie} /> Club Statistics
          </Link>
          <Link to="/reporting-hub" className="chip active">
            <FontAwesomeIcon icon={faChartBarIcon} /> Reporting Hub
          </Link>
          <Link to="/member-database" className="chip">
            <FontAwesomeIcon icon={faUsers} /> Member Database
          </Link>
          <Link to="/category-selection" className="chip">
            <FontAwesomeIcon icon={faListAlt} /> Category Selections
          </Link>
          <Link to="/tickets-admin" className="chip">
            <FontAwesomeIcon icon={faTicketAlt} /> Tickets Admin
          </Link>
        </div>

        {/* Project list */}
        <section className="panel">
          <div className="section-head">
            <h2>All Projects</h2>
            <span className="chip">{loading ? "Loading…" : projects.length}</span>
          </div>

          {loading ? (
            <div className="empty-state">
              <span className="spinner lg" />
              <p>Loading projects…</p>
            </div>
          ) : err ? (
            <div className="error-banner" style={{ margin: "0 var(--sp-4) var(--sp-4)" }}>{err}</div>
          ) : projects.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><FaChartBar /></div>
              <h3>No projects found</h3>
              <p>Once projects are created they will show up here for reporting.</p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Leader</th>
                    <th>Status</th>
                    <th>Phase</th>
                    <th>Created</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, idx) => {
                    const pid = p.id ?? p.project_id ?? p.ID ?? idx;
                    const leaderId = String(pickLeaderSliitId(p) || "").trim();
                    const leaderName =
                      (leaderId &&
                        (nameMap[leaderId] ||
                          nameMap[leaderId.toUpperCase()] ||
                          nameMap[leaderId.toLowerCase()] ||
                          nameMap[leaderId.trim()])) ||
                      p.leader_name ||
                      p.created_by_name ||
                      "";
                    const leaderDisplay = leaderName ? leaderName : leaderId ? leaderId : "Unknown";

                    const status = (p.status || "New").toString();
                    const dateStr = p.created_at ? String(p.created_at).split(" ")[0] : "";
                    const phaseCount = getPhaseCount(p);

                    const openProject = () => navigate(`/project/${pid}`);

                    return (
                      <tr
                        key={pid}
                        role="button"
                        tabIndex={0}
                        onClick={openProject}
                        onKeyDown={(e) => { if (e.key === "Enter") openProject(); }}
                        style={{ cursor: "pointer" }}
                        title="Open project"
                      >
                        <td>
                          <div className="proj-name truncate" style={{ maxWidth: 260 }} title={p.name || "Untitled"}>
                            {p.name || "Untitled"}
                          </div>
                        </td>
                        <td>
                          <span className="chip" style={{ cursor: "default" }} title={leaderId ? `SLIITID: ${leaderId}` : undefined}>
                            {leaderDisplay}
                          </span>
                        </td>
                        <td>
                          <span className={statusPillClass(status)}>
                            <span className="dot" />{status}
                          </span>
                        </td>
                        <td className="eyebrow">Phase {phaseCount}</td>
                        <td className="num" style={{ color: "var(--text-2)" }}>{dateStr || "—"}</td>
                        <td>
                          <div className="row-actions">
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={(e) => { e.stopPropagation(); openProject(); }}
                              title="Open project"
                              aria-label={`Open ${p.name || "project"}`}
                            >
                              <FaChevronRight size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
